import { useRef, useState, MouseEvent, FC } from 'react';
import {
  SavedReply,
  chatActions,
  useAppDispatch,
} from '@vs-online-only-app/state';
import { useIsOverflow } from '@vs-online-only-app/ui';
import { ButtonBase, Typography, Link as MuiLink, Stack } from '@mui/material';
import styled, { css } from 'styled-components';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

type ContentProps = { $isExpanded: boolean };

const expandedStyles = css<ContentProps>`
  ${({ $isExpanded }) =>
    !$isExpanded &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
    `}
`;

const ReplyContent = styled(Typography)<ContentProps>`
  ${expandedStyles}
`;

const Container = styled(ButtonBase)`
  background-color: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2)};
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
`;

type Props = {
  savedReply: SavedReply;
};

const MessageComposerSavedReplyItem: FC<Props> = ({ savedReply }) => {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(ref);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [disableRipple, setDisableRipple] = useState<boolean>(false);

  const handleClick = () => {
    dispatch(chatActions.setSelectedSavedReplyContent(savedReply.content));
    dispatch(chatActions.setSavedRepliesModalOpen(false));
  };

  return (
    <Container onClick={handleClick} disableRipple={disableRipple}>
      <Stack gap={1}>
        <Typography
          fontWeight="bold"
          variant="body2"
          color="textSecondary"
          align="left"
        >
          {savedReply.title}
        </Typography>
        <ReplyContent
          $isExpanded={isExpanded}
          ref={ref}
          variant="body2"
          color="textSecondary"
          align="left"
        >
          {savedReply.content}
        </ReplyContent>
      </Stack>
      {isOverflow && (
        <MuiLink
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }}
          onMouseEnter={() => setDisableRipple(true)}
          onMouseLeave={() => setDisableRipple(false)}
          component="span"
          variant="body2"
        >
          <Stack alignItems="center" direction="row" gap={1}>
            Show {isExpanded ? 'less' : 'more'}
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Stack>
        </MuiLink>
      )}
    </Container>
  );
};

export default MessageComposerSavedReplyItem;
